import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFormResponses } from "@/hooks/useFormResponses";
import { Field, TextInput, YesNo } from "@/components/Field";
import { FieldHelpButton } from "@/components/FieldHelpButton";
import { combineFieldErrors, combineFormError, runUpserts } from "@/lib/validation-errors";

/**
 * Step — Preparer information (Part 13 of N-400).
 *
 * Filled only when someone other than the applicant prepared the form
 * (an attorney, accredited representative, or family member). Self-
 * prepared applications skip this section. The mapping leaves Part 13
 * blank when no preparer_* keys are set.
 *
 * Same toggle-from-existing-data shape as StepInterpreter.
 */

const FR_KEYS = {
  family: "preparer_family_name",
  given: "preparer_given_name",
  org: "preparer_org",
  phone: "preparer_phone",
  mobile: "preparer_mobile",
  email: "preparer_email",
  signatureDate: "preparer_signature_date",
} as const;

export function StepPreparer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fr = useFormResponses(id);

  const [hadPreparer, setHadPreparer] = useState<boolean | null>(null);
  const [family, setFamily] = useState("");
  const [given, setGiven] = useState("");
  const [org, setOrg] = useState("");
  const [phone, setPhone] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [signatureDate, setSignatureDate] = useState("");

  useEffect(() => {
    if (fr.isLoading) return;
    const f = fr.getString(FR_KEYS.family);
    setFamily(f);
    setGiven(fr.getString(FR_KEYS.given));
    setOrg(fr.getString(FR_KEYS.org));
    setPhone(fr.getString(FR_KEYS.phone));
    setMobile(fr.getString(FR_KEYS.mobile));
    setEmail(fr.getString(FR_KEYS.email));
    setSignatureDate(fr.getString(FR_KEYS.signatureDate));
    if (f) setHadPreparer(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fr.isLoading, ...Object.values(FR_KEYS).map((k) => fr.get(k))]);

  const [validationErr, setValidationErr] = useState<string | null>(null);

  // Errors from the most recent save attempt. Local state (not
  // fr.upsert.error) because multiple parallel upserts share that
  // mutation hook and a later succeeding call clears the earlier
  // failing call's error — see runUpserts in validation-errors.ts.
  const [saveErrors, setSaveErrors] = useState<unknown[]>([]);
  const frFieldErrors = combineFieldErrors(saveErrors);
  const frFormError = combineFormError(saveErrors);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErr(null);
    setSaveErrors([]);

    if (hadPreparer === null) {
      setValidationErr("Please answer whether someone other than you prepared this form.");
      return;
    }

    const upserts: Promise<unknown>[] = [];
    const upsertStr = (key: string, val: string) => {
      if (val) upserts.push(fr.upsert.mutateAsync({ fieldKey: key, value: val }));
    };
    if (hadPreparer === true) {
      upsertStr(FR_KEYS.family, family);
      upsertStr(FR_KEYS.given, given);
      upsertStr(FR_KEYS.org, org);
      upsertStr(FR_KEYS.phone, phone);
      upsertStr(FR_KEYS.mobile, mobile);
      upsertStr(FR_KEYS.email, email);
      upsertStr(FR_KEYS.signatureDate, signatureDate);
    }
    const errors = await runUpserts(upserts);
    if (errors.length > 0) {
      setSaveErrors(errors);
      return;
    }
    navigate(`/cases/${id}/wizard/checkout`);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Preparer</h1>
        <p className="text-sm text-slate-500 mt-1">
          USCIS asks whether someone other than you filled out the form
          on your behalf — an attorney, accredited representative, or
          family member. Most self-help applicants skip this section.
        </p>
      </header>

      <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <legend className="font-medium text-slate-900 px-1 flex flex-wrap items-center gap-x-1">
          Did someone other than you prepare this form?
          <FieldHelpButton question="Does using GetCitizen AI to fill out my N-400 count as having a preparer?" />
        </legend>
        <YesNo value={hadPreparer} onChange={setHadPreparer} />
      </fieldset>

      {hadPreparer === true && (
        <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <legend className="font-medium text-slate-900 px-1">Preparer details</legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="Family (last) name"
              required
              error={frFieldErrors?.[FR_KEYS.family]?.message}
            >
              <TextInput value={family} onChange={(e) => setFamily(e.target.value)} />
            </Field>
            <Field
              label="Given (first) name"
              required
              error={frFieldErrors?.[FR_KEYS.given]?.message}
            >
              <TextInput value={given} onChange={(e) => setGiven(e.target.value)} />
            </Field>
            <Field
              label="Business or organisation"
              hint="Law firm, BIA-accredited org, etc. Leave blank if a family member helped."
              className="sm:col-span-2"
              error={frFieldErrors?.[FR_KEYS.org]?.message}
            >
              <TextInput value={org} onChange={(e) => setOrg(e.target.value)} />
            </Field>
            <Field
              label="Daytime telephone"
              required
              error={frFieldErrors?.[FR_KEYS.phone]?.message}
            >
              <TextInput
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Field>
            <Field
              label="Mobile telephone"
              error={frFieldErrors?.[FR_KEYS.mobile]?.message}
            >
              <TextInput
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            </Field>
            <Field
              label="Email address"
              className="sm:col-span-2"
              error={frFieldErrors?.[FR_KEYS.email]?.message}
            >
              <TextInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field
              label="Date of preparer's signature"
              required
              className="sm:col-span-2"
              error={frFieldErrors?.[FR_KEYS.signatureDate]?.message}
            >
              <TextInput
                type="date"
                value={signatureDate}
                onChange={(e) => setSignatureDate(e.target.value)}
              />
            </Field>
          </div>
        </fieldset>
      )}

      {validationErr && (
        <p className="text-sm text-red-600">{validationErr}</p>
      )}
      {frFormError && (
        <p className="text-sm text-red-600">Could not save: {frFormError}</p>
      )}
      {frFieldErrors && (
        <p className="text-xs text-red-600">
          Some fields above need attention before continuing.
        </p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <Link
          to={`/cases/${id}/wizard/interpreter`}
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          ← Interpreter
        </Link>
        <button
          type="submit"
          disabled={fr.upsert.isPending}
          className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {fr.upsert.isPending ? "Saving…" : "Save & continue"}
        </button>
      </div>
    </form>
  );
}
